import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTimeEntries } from '@/hooks/useTimeEntries';
import { useActiveTracking } from '@/hooks/useActiveTracking';
import { useTasks } from '@/hooks/useTasks';
import { useCommesse } from '@/hooks/useCommesse';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  Clock, MessageSquare, Calendar, List, CheckCircle2, LogOut, Zap, Plus,
  Skull, Flame, Crown, Target, Trophy, Swords, Shield, Timer,
  Sparkles, Star, Coffee, Moon, Sun, AlertCircle, Handshake, Gift,
  TrendingUp, Activity, UserPlus, Info, Bell, Loader2,
  Link as LinkIcon, ExternalLink, CalendarDays, AlertTriangle, PauseCircle
} from 'lucide-react';

import { toast, Toaster } from 'sonner';
import { useBrowserNotifications } from '@/hooks/useBrowserNotifications';
import { PushNotificationBanner, PushNotificationToggle } from '@/components/PushNotificationBanner';
import { AchievementMini, AchievementPopup } from '@/components/AchievementBadges';
import { useAchievements } from '@/hooks/useAchievements';
import InterrogationChat from '@/components/InterrogationChat';

import QuickEntry from '@/components/QuickEntry';
import CalendarView from '@/components/CalendarView';
import HistoryView from '@/components/HistoryView';
import NegotiationModal from '@/components/NegotiationModal';
import PendingReasonModal from '@/components/PendingReasonModal';
import WheelOfFortune from '@/components/WheelOfFortune';
import SnitchReports from '@/components/SnitchReports';
import TimeTracker from '@/components/TimeTracker';
import axios from 'axios';


import { API } from '@/config/api';

const MAFIA_GREETINGS = {
  morning: [
    "Bonjour compare! ☕🥐 Il caffè è pronto e il Don ti aspetta con un croissant!",
    "Svegliati! 🌅🇫🇷 La Famiglia italo-francese ha bisogno di te oggi!",
    "È l'alba di un nuovo giorno di... 'lavoro'! ☀️🤌 Allez, forza!",
  ],
  afternoon: [
    "Buon pomeriggio! 🌞🇫🇷 Come procedono gli 'affari' oggi, mon ami?",
    "Il sole è alto e il Don ti osserva... dalla sua villa sulla Costa Azzurra! ☀️🗼",
    "Metà giornata passata... quanto hai fatturato? Il Don vuole comprare una baguetteria! 💰🥖",
  ],
  evening: [
    "Bonsoir compare! 🌙🇫🇷 È ora di tirare le somme... avec style!",
    "Il sole tramonta ma il lavoro non finisce mai! Così dice il Don! 🌅🤌",
    "La notte si avvicina... hai fatto il tuo dovere? Le stelle di Parigi ti guardano! 🦉🗼",
  ],
};

const TASK_COMPLETE_PHRASES = [
  "🎉🇫🇷 MAGNIFIQUE! Il Don è ORGOGLIOSO di te!",
  "💪🥐 FATTO! Un vrai soldat della Famiglia!",
  "🏆🗼 COMPLETATO! Meriti un bicchiere di Bordeaux!",
  "⭐🇫🇷 EXCELLENT! Il Don ti farà una promozione... forse!",
  "🎊🤌 BRAVO COMPARE! La Famiglia festeggia con champagne!",
];

const TASK_START_PHRASES = [
  "Ho informato quello snitch di Andrea... buon lavoro!",
  "Il Don sa che hai iniziato. Non farti trovare a dormire... 🤌",
  "Ordine registrato. Corri, prima che Andrea faccia un'altra soffiata! 🐍",
  "Affare in corso. Andrea sta già scrivendo il suo rapporto... 🖋️",
  "Lavoro avviato. Se fallisci, Andrea sarà il primo a saperlo! 💀",
  "Allez! La Famiglia conta su di te. Andrea invece conta i tuoi errori! 🥐",
];

const MemberDashboard = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('home');
  const { entries, refetch: refetchEntries, createEntry, deleteEntry } = useTimeEntries(user?.id);
  const { tasks, updateTaskStatus, refetch: refetchTasks } = useTasks(user?.id);
  const { commesse, createCommessa, refetch: refetchCommesse } = useCommesse();

  const { activeTask, startTracking, finishTracking, refetch: refetchActive } = useActiveTracking(user);
  const { permission, requestPermission, sendNotification } = useBrowserNotifications();
  const { newAchievement, dismissNewAchievement } = useAchievements(user?.id);

  // User profile/rispetto
  const [userProfile, setUserProfile] = useState(null);

  // Negotiation modal state
  const [negotiatingTask, setNegotiatingTask] = useState(null);
  const [pausingTask, setPausingTask] = useState(null);

  // Task completion modal state
  const [completingTask, setCompletingTask] = useState(null);
  const [taskHours, setTaskHours] = useState('');
  const [taskDate, setTaskDate] = useState(new Date().toISOString().split('T')[0]);
  const [taskCommessa, setTaskCommessa] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [taskLoading, setTaskLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Task details modal state
  const [selectedTaskDetail, setSelectedTaskDetail] = useState(null);

  // New commessa state
  const [showNewCommessa, setShowNewCommessa] = useState(false);
  const [newCommessa, setNewCommessa] = useState({ code: '', name: '', keywords: '', type: 'SOC' });

  // Timer state
  const [timerString, setTimerString] = useState('00:00:00');

  // Greeting state
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return MAFIA_GREETINGS.morning[Math.floor(Math.random() * MAFIA_GREETINGS.morning.length)];
    if (hour < 18) return MAFIA_GREETINGS.afternoon[Math.floor(Math.random() * MAFIA_GREETINGS.afternoon.length)];
    return MAFIA_GREETINGS.evening[Math.floor(Math.random() * MAFIA_GREETINGS.evening.length)];
  };
  const [greeting] = useState(getGreeting());

  // Timer logic
  const formatTimer = (startTime) => {
    const start = new Date(startTime).getTime();
    const now = new Date().getTime();
    const diff = Math.max(0, now - start);

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (!activeTask) return;
    const interval = setInterval(() => {
      setTimerString(formatTimer(activeTask.start_time));
    }, 1000);
    return () => clearInterval(interval);
  }, [activeTask, setTimerString]);

  // New Task Notifications ref
  const prevTasksRef = useRef([]);

  // Authentication Guard
  useEffect(() => {
    if (!loading && !user) {
      navigate('/');
    }
  }, [user, loading, navigate]);

  // Auto-generate next code when type changes
  useEffect(() => {
    if (showNewCommessa && newCommessa.type) {
      const safeCommesse = Array.isArray(commesse) ? commesse : [];
      const samePrefixCommesse = safeCommesse.filter(c => c.code.startsWith(newCommessa.type + '-'));
      const numbers = samePrefixCommesse
        .map(c => {
          const match = c.code.match(/-0*(\d+)$/);
          return match ? parseInt(match[1], 10) : 0;
        })
        .filter(n => !isNaN(n));

      const nextNumber = numbers.length > 0 ? Math.max(...numbers) + 1 : 1;
      const nextCode = `${newCommessa.type}-${String(nextNumber).padStart(4, '0')}`;

      setNewCommessa(prev => ({ ...prev, code: nextCode }));
    }
  }, [showNewCommessa, newCommessa.type, commesse]);

  // Calculate stats
  const today = new Date().toISOString().split('T')[0];
  const safeEntries = Array.isArray(entries) ? entries : [];
  const todayEntries = safeEntries.filter(e => e.date === today);
  const todayHours = todayEntries.reduce((sum, e) => sum + e.hours, 0);

  const weekStart = new Date();
  weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
  const weekEntries = safeEntries.filter(e => new Date(e.date) >= weekStart);
  const weekHours = weekEntries.reduce((sum, e) => sum + e.hours, 0);

  const safeTasks = Array.isArray(tasks) ? tasks : [];
  const pendingTasks = safeTasks.filter(t => t.status !== 'completed' && t.status !== 'waiting');
  const waitingTasks = safeTasks.filter(t => t.status === 'waiting');
  const completedTasks = safeTasks.filter(t => t.status === 'completed');

  // Fetch user profile with rispetto
  const refreshProfile = useCallback(async () => {
    if (user?.id) {
      try {
        const res = await axios.get(`${API}/profiles/${user.id}/rispetto?user_id=${user.id}`);
        setUserProfile(res.data);
      } catch (err) {
        console.error("Error fetching profile:", err);
      }
    }
  }, [user?.id]);

  useEffect(() => {
    refreshProfile();
  }, [refreshProfile]);

  // New Task Notifications
  useEffect(() => {

    if (pendingTasks.length > prevTasksRef.current.length && prevTasksRef.current.length > 0) {
      // Find the new task
      const newTasks = pendingTasks.filter(t => !prevTasksRef.current.find(pt => pt.id === t.id));

      newTasks.forEach(task => {
        // Browser Notification
        sendNotification("NUOVO ORDINE DAL DON 🤌", {
          body: task.title,
          icon: "/favicon.png",
          tag: `task-${task.id}` // Add tag to prevent duplicate notifications
        });

        // Sonner toast
        toast.warning("🚨 NUOVO ORDINE", {
          description: `È arrivato un nuovo compito: "${task.title}". Non farti aspettare...`,
          duration: 10000,
        });

        // Play sound
        try {
          const audio = new Audio('/sounds/notification.mp3');
          audio.play().catch(e => console.warn("Autoplay blocked:", e));
        } catch (e) {
          console.warn("Could not play sound:", e);
        }
      });
    }
    prevTasksRef.current = pendingTasks;
  }, [pendingTasks, sendNotification]);

  // Scary Notifications for Overdue Tasks
  // Scary Notifications for Overdue Tasks
  const notifiedExpiredRef = useRef(new Set());

  useEffect(() => {
    if (pendingTasks.length > 0) {
      const todayString = new Date().toISOString().split('T')[0];
      const overdue = pendingTasks.filter(t => t.due_date && t.due_date < todayString);

      // Only notify about new expired tasks or if we haven't notified in this session yet
      // Simple logic: if count changes or new ID appears?
      // Actually, just notify ONCE per session per batch is safer.
      const notYetNotified = overdue.filter(t => !notifiedExpiredRef.current.has(t.id));

      if (overdue.length > 0 && notYetNotified.length > 0) {
        // Mark all current overdue as notified
        overdue.forEach(t => notifiedExpiredRef.current.add(t.id));

        setTimeout(() => {
          toast.error("⚠️ MESSAGGIO DAL DON", {
            description: `Hai ${overdue.length} ordini scaduti. Il Padrino non ama l'attesa... né i tradimenti. Sbrigati, compare. 🔪🔥`,
            duration: 8000,
          });
          // Play threat sound for overdue tasks
          try {
            const audio = new Audio('/sounds/threat.mp3');
            audio.play().catch(e => console.warn("Autoplay blocked:", e));
          } catch (e) { }


          try {
            if (Notification.permission === 'granted') {
              // Use helper if checking permission manually, but sendNotification handles it too
              // We use sendNotification from the hook which handles mobile quirks (Service Worker) better
              sendNotification("SCADENZA! 💀", {
                body: `Il Don non perdona. Hai ${overdue.length} ordini scaduti!`,
                icon: "/favicon.png",
                tag: 'expired-alert'
              });
            }
          } catch (e) {
            console.error("Notification failed", e);
          }
        }, 1000);
      }
    }
  }, [pendingTasks, sendNotification]);

  // Loading guard - placed after all hooks
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-amber-500 animate-spin" />
      </div>
    );
  }

  // Null user guard - placed after all hooks  
  if (!user) return null;

  // Get time icon based on hour
  const getTimeIcon = () => {

    const hour = new Date().getHours();
    if (hour < 12) return <Coffee className="w-6 h-6" />;
    if (hour < 18) return <Sun className="w-6 h-6" />;
    return <Moon className="w-6 h-6" />;
  };

  // Handle task completion with time logging
  const openTaskCompletion = (task) => {
    setCompletingTask(task);
    setTaskHours('');
    setTaskDate(new Date().toISOString().split('T')[0]);
    setTaskCommessa('');
    setTaskDescription(task.title + (task.description ? ` - ${task.description}` : ''));
  };

  // Handle negotiation
  const handleNegotiationSuccess = () => {
    refetchTasks();
    refreshProfile();
  };

  const handlePauseConfirm = async (reason) => {
    const res = await updateTaskStatus(pausingTask.id, 'waiting', reason);
    if (res.success) {
      toast.success("⏸️ ORDINE IN PAUSA", { description: `"${reason}"` });
    }
    setPausingTask(null);
  };

  const handleCompleteTask = async () => {
    if (!taskHours || !taskCommessa) return;

    setTaskLoading(true);

    try {
      // Find commessa details
      const safeCommesse = Array.isArray(commesse) ? commesse : [];
      const selectedCommessa = safeCommesse.find(c => c.code === taskCommessa);

      // Create time entry
      await axios.post(`${API}/time-entries`, {
        user_id: user.id,
        user_name: user.name,
        date: taskDate,
        commessa_code: taskCommessa,
        commessa_name: selectedCommessa?.name || taskCommessa,
        project_id: null,
        project_name: null,
        description: taskDescription,
        hours: parseFloat(taskHours),
        notes: `Task completato: ${completingTask.title}`
      });

      // Mark task as completed
      await updateTaskStatus(completingTask.id, 'completed');

      // Show success
      setSuccessMessage(TASK_COMPLETE_PHRASES[Math.floor(Math.random() * TASK_COMPLETE_PHRASES.length)]);
      setShowSuccess(true);

      // Refresh data
      refetchEntries();
      refetchTasks();

      // Close modal after animation
      setTimeout(() => {
        setShowSuccess(false);
        setCompletingTask(null);
      }, 2000);

    } catch (err) {
      console.error('Error completing task:', err);
    } finally {
      setTaskLoading(false);
    }
  };

  const handleCreateCommessa = async () => {
    if (!newCommessa.code || !newCommessa.name) return;
    const result = await createCommessa({
      code: newCommessa.code.toUpperCase(),
      name: newCommessa.name,
      keywords: newCommessa.keywords.split(',').map(k => k.trim().toLowerCase()).filter(k => k)
    });
    if (result.success) {
      setShowNewCommessa(false);
      setNewCommessa({ code: '', name: '', keywords: '', type: 'SOC' });
      // Immediately refresh commesse so it's available in chat
      refetchCommesse();
    }
  };

  const handleEntryCreated = () => {
    refetchEntries();
    setViewMode('home');
  };

  // Fun progress calculation
  const dailyGoal = 8;
  const progressPercent = Math.min((todayHours / dailyGoal) * 100, 100);

  const handleFinishActive = async () => {
    await finishTracking();
    refetchEntries();
    refetchActive();
  };

  const renderActiveTask = () => (
    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-amber-900 via-zinc-900 to-amber-950 p-8 mb-8 border-2 border-amber-500/50 animate-pulse-slow">
      <div className="absolute top-0 right-0 p-4 opacity-20">
        <Timer className="w-32 h-32 text-amber-500" />
      </div>

      <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
        <div>
          <Badge className="bg-amber-500 text-black font-bold mb-4 px-4 py-1 animate-bounce">
            ⚠️ LAVORO IN CORSO
          </Badge>
          <h2 className="text-3xl font-black text-white mb-2 uppercase italic tracking-tighter">
            {activeTask.description}
          </h2>
          <p className="text-amber-400/70 font-display flex items-center gap-2">
            <Target className="w-4 h-4" />
            {activeTask.commessa_name} {activeTask.project_name ? ` - ${activeTask.project_name}` : ''}
          </p>
          <div className="mt-6 flex items-baseline gap-2">
            <span className="text-6xl font-black text-white font-mono">{timerString}</span>
            <span className="text-zinc-500 text-sm">di {activeTask.estimated_hours}h stimate</span>
          </div>
        </div>

        <div className="flex flex-col gap-4 w-full md:w-auto">
          <Button
            size="lg"
            onClick={handleFinishActive}
            className="bg-green-600 hover:bg-green-500 text-white font-black px-12 py-8 text-2xl rounded-2xl shadow-2xl shadow-green-500/40 border-b-4 border-green-800 active:border-b-0 transition-all"
          >
            HO FINITO! 🤌
          </Button>
          <p className="text-center text-xs text-amber-500/50 uppercase tracking-widest font-bold">
            "Non far aspettare il Don"
          </p>
        </div>
      </div>
    </div>
  );

  const renderHome = () => (
    <div className="animate-fade-in">
      {/* Active Task Widget - Now handled by TimeTracker bottom bar */}

      {/* Push Notification Setup Banner */}
      <PushNotificationBanner userId={user?.id} />

      {/* Hero Section - Cinematic */}

      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 p-8 mb-8 border border-primary/20">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-red-500/10 rounded-full blur-3xl" />

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            {getTimeIcon()}
            <span className="text-zinc-400">{greeting}</span>
          </div>

          <h1 className="font-display text-5xl md:text-6xl font-black mb-6 leading-tight">
            <span className="text-white">Bene, </span>
            <span className="gold-text">{user?.name}</span>
            <br />
            <span className="text-4xl text-primary/80">Don Doit ti osserva 👁️🇫🇷</span>
          </h1>

          {/* Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-8">
            <div className="bg-black/30 backdrop-blur rounded-2xl p-4 border border-white/10">
              <div className="flex items-center gap-2 text-primary mb-1">
                <Flame className="w-5 h-5" />
                <span className="text-xs uppercase tracking-wider">Oggi</span>
              </div>
              <p className="text-3xl font-black text-white">{todayHours.toFixed(1)}h</p>
            </div>
            <div className="bg-black/30 backdrop-blur rounded-2xl p-4 border border-white/10">
              <div className="flex items-center gap-2 text-zinc-400 mb-1">
                <Trophy className="w-5 h-5" />
                <span className="text-xs uppercase tracking-wider">Settimana</span>
              </div>
              <p className="text-3xl font-black text-white">{weekHours.toFixed(1)}h</p>
            </div>
            <div className="bg-black/30 backdrop-blur rounded-2xl p-4 border border-white/10">
              <div className="flex items-center gap-2 text-zinc-400 mb-1">
                <Target className="w-5 h-5" />
                <span className="text-xs uppercase tracking-wider">Tasks</span>
              </div>
              <p className="text-3xl font-black text-white">{pendingTasks.length}</p>
            </div>
            <div className="bg-black/30 backdrop-blur rounded-2xl p-4 border border-white/10">
              <div className="flex items-center gap-2 text-green-400 mb-1">
                <CheckCircle2 className="w-5 h-5" />
                <span className="text-xs uppercase tracking-wider">Completati</span>
              </div>
              <p className="text-3xl font-black text-white">{completedTasks.length}</p>
            </div>
            {/* RISPETTO CARD */}
            <div className="bg-gradient-to-br from-amber-900/30 to-amber-950/30 backdrop-blur rounded-2xl p-4 border border-amber-500/30">
              <div className="flex items-center gap-2 text-amber-400 mb-1">
                <Crown className="w-5 h-5" />
                <span className="text-xs uppercase tracking-wider">Rispetto</span>
              </div>
              <p className="text-3xl font-black text-amber-400">{userProfile?.rispetto || 100}</p>
              <p className="text-xs text-amber-500/70 truncate mt-1">{userProfile?.current_title || 'Soldato'}</p>
            </div>
            {/* ACHIEVEMENTS MINI */}
            <div className="bg-gradient-to-br from-purple-900/30 to-purple-950/30 backdrop-blur rounded-2xl p-4 border border-purple-500/30">
              <AchievementMini userId={user?.id} />
            </div>
          </div>

          {/* Daily Progress Bar */}
          <div className="mt-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-zinc-400">Obiettivo giornaliero: {dailyGoal}h</span>
              <span className="text-sm font-bold gold-text">{progressPercent.toFixed(0)}%</span>
            </div>
            <div className="h-3 bg-black/50 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-primary to-yellow-400 rounded-full transition-all duration-1000"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            {progressPercent >= 100 && (
              <p className="text-sm text-green-400 mt-2 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Obiettivo raggiunto! Il Padrino è fiero di te! 🎉
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Leaderboard - NEW Rispetto 2.0 */}
      <div className="mb-8 overflow-hidden rounded-2xl bg-zinc-900/50 border border-zinc-800">
        <div className="p-4 border-b border-zinc-800 bg-zinc-900/80 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500" />
            <h3 className="font-display font-bold text-white uppercase tracking-wider text-sm">Gerarchia della Famiglia</h3>
          </div>
          <Badge variant="outline" className="border-amber-500/30 text-amber-400">
            Il tuo rango: {userProfile?.tier || 'Associato'}
          </Badge>
        </div>
        <div className="p-4">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 rounded-full bg-amber-500/10 border border-amber-500/30">
              <Crown className="w-8 h-8 text-amber-500" />
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-end mb-1">
                <span className="text-xs text-zinc-500 font-bold uppercase">Progressi Rispetto</span>
                <span className="text-xl font-black text-amber-500">{userProfile?.points || 0} pts</span>
              </div>
              <div className="h-2 bg-black/50 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-amber-600 to-amber-400"
                  style={{ width: `${Math.min((userProfile?.points || 0) / 2000 * 100, 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons - Big and Fun */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <button
          onClick={() => setViewMode('chat')}
          className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-red-900 to-red-950 p-6 border border-red-500/30 hover:border-red-500/60 transition-all hover:scale-[1.02] active:scale-[0.98]"
          data-testid="chat-button"
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          <div className="relative z-10 text-center">
            <Skull className="w-12 h-12 mx-auto mb-3 text-red-400 group-hover:scale-110 transition-transform" />
            <h3 className="font-display text-xl font-bold text-white mb-1">IL DON</h3>
            <p className="text-xs text-red-300/70">Parla... se osi</p>
          </div>
        </button>

        <button
          onClick={() => setViewMode('quick')}
          className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-900 to-amber-950 p-6 border border-amber-500/30 hover:border-amber-500/60 transition-all hover:scale-[1.02] active:scale-[0.98]"
          data-testid="quick-entry-button"
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          <div className="relative z-10 text-center">
            <Zap className="w-12 h-12 mx-auto mb-3 text-amber-400 group-hover:scale-110 transition-transform" />
            <h3 className="font-display text-xl font-bold text-white mb-1">VELOCE</h3>
            <p className="text-xs text-amber-300/70">Registra subito</p>
          </div>
        </button>

        <button
          onClick={() => setViewMode('calendar')}
          className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-900 to-blue-950 p-6 border border-blue-500/30 hover:border-blue-500/60 transition-all hover:scale-[1.02] active:scale-[0.98]"
          data-testid="calendar-button"
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          <div className="relative z-10 text-center">
            <Calendar className="w-12 h-12 mx-auto mb-3 text-blue-400 group-hover:scale-110 transition-transform" />
            <h3 className="font-display text-xl font-bold text-white mb-1">CALENDARIO</h3>
            <p className="text-xs text-blue-300/70">Vista mensile</p>
          </div>
        </button>

        <button
          onClick={() => setViewMode('history')}
          className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-900 to-purple-950 p-6 border border-purple-500/30 hover:border-purple-500/60 transition-all hover:scale-[1.02] active:scale-[0.98]"
          data-testid="history-button"
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          <div className="relative z-10 text-center">
            <List className="w-12 h-12 mx-auto mb-3 text-purple-400 group-hover:scale-110 transition-transform" />
            <h3 className="font-display text-xl font-bold text-white mb-1">STORICO</h3>
            <p className="text-xs text-purple-300/70">Tutte le ore</p>
          </div>
        </button>
      </div>

      {/* FUN ZONE */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        <WheelOfFortune userId={user?.id} onSpinComplete={() => refreshProfile()} />
        <SnitchReports />
      </div>


      {/* TASKS Section - AGENDA VIEW */}
      {pendingTasks.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="flex items-center gap-1">
              <Swords className="w-6 h-6 text-red-500" />
              <Shield className="w-6 h-6 text-red-500" />
            </div>
            <h2 className="font-display text-2xl font-bold text-white">
              ORDINI DALLA FAMIGLIA
            </h2>
            <Badge variant="destructive" className="animate-pulse">
              {pendingTasks.length} PENDENTI
            </Badge>
          </div>

          <div className="space-y-8">
            {/* Grouping Logic */}
            {(() => {
              const now = new Date();
              const todayStr = now.toISOString().split('T')[0];
              const tomorrow = new Date(now);
              tomorrow.setDate(tomorrow.getDate() + 1);
              const tomorrowStr = tomorrow.toISOString().split('T')[0];

              const startOfWeek = new Date(now);
              startOfWeek.setDate(now.getDate() - now.getDay() + 1);
              const endOfWeek = new Date(startOfWeek);
              endOfWeek.setDate(startOfWeek.getDate() + 6);

              const groups = {
                expired: [],
                today: [],
                tomorrow: [],
                week: [],
                future: []
              };

              pendingTasks.forEach(task => {
                if (!task.due_date) {
                  groups.future.push(task);
                  return;
                }
                if (task.due_date < todayStr) {
                  groups.expired.push(task);
                } else if (task.due_date === todayStr) {
                  groups.today.push(task);
                } else if (task.due_date === tomorrowStr) {
                  groups.tomorrow.push(task);
                } else if (new Date(task.due_date) <= endOfWeek) {
                  groups.week.push(task);
                } else {
                  groups.future.push(task);
                }
              });

              const renderTaskGroup = (title, tasks, icon, colorClass, borderClass) => {
                if (tasks.length === 0) return null;
                return (
                  <div className="animate-fade-in">
                    <div className={`flex items-center gap-2 mb-3 border-b ${borderClass} pb-2`}>
                      {icon}
                      <h3 className={`font-display font-bold uppercase tracking-wider ${colorClass}`}>{title}</h3>
                      <Badge variant="outline" className={`${colorClass} border-current`}>{tasks.length}</Badge>
                    </div>
                    <div className="grid gap-4">
                      {tasks.map((task) => (
                        <div
                          key={task.id}
                          className="relative overflow-hidden rounded-2xl bg-zinc-900 border border-zinc-800 hover:border-zinc-600 transition-all p-5 group"
                        >
                          <div className={`absolute top-0 left-0 w-1 h-full ${colorClass.replace('text-', 'bg-')}`} />

                          <div className="flex flex-col md:flex-row gap-4 justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge className="bg-zinc-800 text-zinc-400 text-[10px] hover:bg-zinc-700">
                                  da {task.assigned_by_name}
                                </Badge>
                                {task.due_date && (
                                  <span className={`text-xs font-bold ${colorClass} flex items-center gap-1`}>
                                    <Timer className="w-3 h-3" />
                                    {task.due_date.split('-').reverse().join('/')}
                                  </span>
                                )}
                              </div>

                              <h3
                                onClick={() => setSelectedTaskDetail(task)}
                                className="text-xl font-bold text-white mb-2 group-hover:text-primary transition-colors cursor-pointer"
                              >
                                {task.title}
                              </h3>

                              {task.description && (
                                <p
                                  onClick={() => setSelectedTaskDetail(task)}
                                  className="text-sm text-zinc-400 mb-3 line-clamp-2 cursor-pointer"
                                >
                                  {task.description}
                                </p>
                              )}

                              {/* PENDING REASON */}
                              {task.status === 'waiting' && task.pending_reason && (
                                <div className="flex items-center gap-1.5 mb-2">
                                  <PauseCircle className="w-3 h-3 text-red-400/70 flex-shrink-0" />
                                  <p className="text-xs text-red-300/70 italic">{task.pending_reason}</p>
                                </div>
                              )}

                              {/* DON THREAT */}
                              <div className="bg-black/30 rounded-lg p-2 border-l-2 border-red-500/20 mb-3 inline-block max-w-full">
                                <p className="text-xs text-zinc-500 italic truncate">
                                  "{task.don_threat}"
                                </p>
                              </div>

                              {/* LINKS SECTION */}
                              {task.links && task.links.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-2">
                                  {task.links.map((linkStr, i) => {
                                    const parts = linkStr.split('|');
                                    let label = 'Link';
                                    let url = '#';
                                    if (parts.length === 2) {
                                      label = parts[0];
                                      url = parts[1];
                                    } else {
                                      url = linkStr; // Fallback for plain URLs
                                    }

                                    return (
                                      <a
                                        key={i}
                                        href={url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-white rounded-lg text-xs font-medium transition-colors border border-zinc-700"
                                      >
                                        <LinkIcon className="w-3 h-3 text-blue-400" />
                                        {label}
                                        <ExternalLink className="w-3 h-3 opacity-50" />
                                      </a>
                                    );
                                  })}
                                </div>
                              )}
                            </div>

                            <div className="flex flex-row md:flex-col gap-2 w-full md:w-auto mt-4 md:mt-0">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setSelectedTaskDetail(task)}
                                className="border-zinc-700 text-zinc-400 hover:bg-zinc-800 flex-1 whitespace-nowrap"
                              >
                                <Info className="w-4 h-4 mr-2" />
                                DETTAGLI
                              </Button>
                              {(task.status === 'pending' || task.status === 'waiting') && (
                                <Button
                                  size="sm"
                                  onClick={async () => {
                                    const res = await updateTaskStatus(task.id, 'in_progress');
                                    if (res.success) {
                                      const phrase = TASK_START_PHRASES[Math.floor(Math.random() * TASK_START_PHRASES.length)];
                                      toast.success("🔥 ORDINE INIZIATO", {
                                        description: `${phrase}`,
                                      });
                                    }
                                  }}
                                  className="bg-amber-600 hover:bg-amber-500 text-white font-bold flex-1 whitespace-nowrap"
                                >
                                  <Timer className="w-4 h-4 mr-2" />
                                  INIZIA
                                </Button>
                              )}
                              <Button
                                size="sm"
                                onClick={() => openTaskCompletion(task)}
                                className="bg-green-600 hover:bg-green-500 text-white font-bold shadow-lg shadow-green-900/20 flex-1 whitespace-nowrap"
                              >
                                <CheckCircle2 className="w-4 h-4 mr-2" />
                                FATTO
                              </Button>
                              {task.due_date && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setNegotiatingTask(task)}
                                  className="border-amber-500/20 text-amber-500 hover:bg-amber-500/10 flex-1 whitespace-nowrap"
                                >
                                  <Handshake className="w-4 h-4 mr-2" />
                                  TRATTA
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              };

              return (
                <div className="space-y-8">
                  {renderTaskGroup("💀 SCADUTI", groups.expired, <Skull className="w-5 h-5 text-red-500" />, "text-red-500", "border-red-500/30")}
                  {renderTaskGroup("🔥 OGGI", groups.today, <Flame className="w-5 h-5 text-amber-500" />, "text-amber-500", "border-amber-500/30")}
                  {renderTaskGroup("🗓️ DOMANI", groups.tomorrow, <CalendarDays className="w-5 h-5 text-yellow-400" />, "text-yellow-400", "border-yellow-400/30")}
                  {renderTaskGroup("📅 QUESTA SETTIMANA", groups.week, <Calendar className="w-5 h-5 text-blue-400" />, "text-blue-400", "border-blue-400/30")}
                  {renderTaskGroup("🔮 FUTURO", groups.future, <Sparkles className="w-5 h-5 text-purple-400" />, "text-purple-400", "border-purple-400/30")}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* IN PAUSA Section */}
      {waitingTasks.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-6">
            <PauseCircle className="w-6 h-6 text-blue-400" />
            <h2 className="font-display text-2xl font-bold text-white">IN PAUSA</h2>
            <Badge className="bg-blue-500/20 text-blue-400">{waitingTasks.length}</Badge>
          </div>
          <div className="grid gap-4">
            {waitingTasks.map((task) => (
              <div
                key={task.id}
                className="relative overflow-hidden rounded-2xl bg-zinc-900 border border-blue-500/20 p-5"
              >
                <div className="absolute top-0 left-0 w-1 h-full bg-blue-500/40" />
                <div className="flex flex-col md:flex-row gap-4 justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className="bg-zinc-800 text-zinc-400 text-[10px]">da {task.assigned_by_name}</Badge>
                    </div>
                    <h3
                      onClick={() => setSelectedTaskDetail(task)}
                      className="text-xl font-bold text-white mb-2 cursor-pointer hover:text-primary transition-colors"
                    >
                      {task.title}
                    </h3>
                    {task.pending_reason && (
                      <div className="flex items-center gap-1.5 mb-2">
                        <PauseCircle className="w-3 h-3 text-blue-400/70 flex-shrink-0" />
                        <p className="text-xs text-blue-300/70 italic">{task.pending_reason}</p>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-row md:flex-col gap-2 w-full md:w-auto mt-4 md:mt-0">
                    <Button
                      size="sm"
                      onClick={async () => {
                        const res = await updateTaskStatus(task.id, 'in_progress');
                        if (res.success) {
                          const phrase = TASK_START_PHRASES[Math.floor(Math.random() * TASK_START_PHRASES.length)];
                          toast.success("🔥 ORDINE RIPRESO", { description: `${phrase}` });
                        }
                      }}
                      className="bg-amber-600 hover:bg-amber-500 text-white font-bold flex-1 whitespace-nowrap"
                    >
                      <Timer className="w-4 h-4 mr-2" />
                      RIPRENDI
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => openTaskCompletion(task)}
                      className="bg-green-600 hover:bg-green-500 text-white font-bold flex-1 whitespace-nowrap"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      FATTO
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Today's Work */}
      {todayEntries.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Clock className="w-5 h-5 text-primary" />
            <h2 className="font-display text-xl font-bold text-white">LE TUE FATICHE DI OGGI</h2>
          </div>

          <div className="grid gap-3">
            {todayEntries.map((entry) => (
              <div
                key={entry.id}
                className="flex items-center justify-between p-4 bg-zinc-900/50 rounded-xl border border-zinc-800 hover:border-primary/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                    <Star className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-white">{entry.commessa_name}</p>
                    {entry.project_name && (
                      <p className="text-xs text-zinc-500">{entry.project_name}</p>
                    )}
                  </div>
                </div>
                <Badge className="bg-primary/20 text-primary border-primary/30 text-lg px-3 py-1">
                  {entry.hours}h
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Warning if no hours */}
      {todayHours === 0 && (
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-red-950 to-zinc-900 border border-red-500/50 p-8 text-center">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLW9wYWNpdHk9IjAuMDMiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-50" />

          <div className="relative z-10">
            <div className="text-6xl mb-4">💀🔥💀</div>
            <h3 className="font-display text-2xl font-bold text-red-400 mb-2">
              ATTENZIONE COMPARE!
            </h3>
            <p className="text-zinc-400 mb-4">
              Nessuna ora registrata oggi... Il Padrino sta già affilando il coltello...
            </p>
            <p className="text-sm text-red-400/70 italic">
              "Chi non lavora, non mangia" - Legge della Famiglia
            </p>
          </div>
        </div>
      )}

      {/* Add Commessa Button */}
      <div className="flex justify-center mt-8">
        <Dialog open={showNewCommessa} onOpenChange={setShowNewCommessa}>
          <DialogTrigger asChild>
            <Button variant="outline" className="border-dashed border-zinc-700 text-zinc-400 hover:border-primary hover:text-primary" data-testid="new-commessa-button">
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi Nuova Commessa
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="font-display text-xl text-white flex items-center gap-2">
                🤌 Nuova Commessa
              </DialogTitle>
              <DialogDescription className="text-zinc-500 text-xs">
                Inserisci i dettagli del nuovo cliente o progetto per la Famiglia.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <label className="text-sm text-zinc-400 mb-2 block">Tipo Commessa</label>
                <Select
                  value={newCommessa.type}
                  onValueChange={(type) => setNewCommessa(prev => ({ ...prev, type }))}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="SOC" className="text-white">SOC - Social Media</SelectItem>
                    <SelectItem value="VID" className="text-white">VID - Video Production</SelectItem>
                    <SelectItem value="WEB" className="text-white">WEB - Web Development</SelectItem>
                    <SelectItem value="GFX" className="text-white">GFX - Graphic Design</SelectItem>
                    <SelectItem value="MKT" className="text-white">MKT - Marketing</SelectItem>
                    <SelectItem value="ADM" className="text-white">ADM - Amministrazione</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-2 block">Codice (auto-generato)</label>
                <Input
                  value={newCommessa.code}
                  onChange={(e) => setNewCommessa(prev => ({ ...prev, code: e.target.value }))}
                  placeholder="SOC-0007"
                  className="bg-zinc-800 border-zinc-700 text-white font-mono"
                  data-testid="new-commessa-code"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-2 block">Nome Cliente</label>
                <Input
                  value={newCommessa.name}
                  onChange={(e) => setNewCommessa(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Nome del cliente..."
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="new-commessa-name"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-2 block">Keywords (separate da virgola)</label>
                <Input
                  value={newCommessa.keywords}
                  onChange={(e) => setNewCommessa(prev => ({ ...prev, keywords: e.target.value }))}
                  placeholder="keyword1, keyword2..."
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="new-commessa-keywords"
                />
              </div>
              <Button
                className="w-full bg-primary hover:bg-primary/90 text-black font-bold"
                onClick={handleCreateCommessa}
                disabled={!newCommessa.code || !newCommessa.name}
                data-testid="save-commessa-button"
              >
                Aggiungi alla Famiglia 🤌
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-zinc-950">
      <Toaster position="top-center" richColors theme="dark" />
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-900/50 backdrop-blur sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {viewMode !== 'home' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setViewMode('home')}
                className="text-zinc-400 hover:text-white"
                data-testid="back-home-button"
              >
                ← Indietro
              </Button>
            )}
            <h1
              className="font-display text-xl font-bold cursor-pointer flex items-center gap-2"
              onClick={() => setViewMode('home')}
            >
              <span className="text-2xl">🤌🇫🇷</span>
              <span className="gold-text">Don Doit</span>
            </h1>
          </div>

          <div className="flex items-center gap-3">
            <Badge variant="outline" className="border-primary/50 text-primary hidden md:flex">
              {user?.name}
            </Badge>

            {/* Time Tracker START button (only shown when no active task) */}
            {(!activeTask || activeTask.status !== 'active') && (
              <TimeTracker
                activeTask={activeTask}
                startTracking={startTracking}
                finishTracking={finishTracking}
                commesse={commesse}
                user={user}
              />
            )}

            <div className="hidden sm:flex items-center">
              <PushNotificationToggle userId={user?.id} />
            </div>

            {/* Manual button removed in favor of PushNotificationToggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              className="text-zinc-400 hover:text-red-400"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-6xl mx-auto p-4">
        {viewMode === 'home' && renderHome()}
        {viewMode === 'chat' && (
          <InterrogationChat
            user={user}
            commesse={commesse}
            refetchCommesse={refetchCommesse}
            onEntryCreated={() => {
              handleEntryCreated();
              refetchActive();
            }}
          />
        )}
        {viewMode === 'quick' && (
          <QuickEntry
            user={user}
            commesse={commesse}
            onEntryCreated={handleEntryCreated}
          />
        )}
        {viewMode === 'calendar' && (
          <CalendarView
            entries={entries}
            onDeleteEntry={deleteEntry}
          />
        )}
        {viewMode === 'history' && (
          <HistoryView
            entries={entries}
            onDeleteEntry={deleteEntry}
          />
        )}
      </main>

      {/* Task Completion Modal */}
      <Dialog open={!!completingTask} onOpenChange={() => setCompletingTask(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          {showSuccess ? (
            <div className="text-center py-8 animate-fade-in">
              <div className="text-6xl mb-4">🎉</div>
              <h3 className="font-display text-2xl font-bold gold-text mb-2">
                {successMessage}
              </h3>
              <p className="text-zinc-400">Ore registrate con successo!</p>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl text-white flex items-center gap-2">
                  <CheckCircle2 className="w-6 h-6 text-green-500" />
                  Completa il Task
                </DialogTitle>
                <DialogDescription className="text-zinc-500 text-xs">
                  Registra le ore e l'attività svolta per chiudere questo ordine.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 pt-4">
                {/* Task info */}
                <div className="bg-zinc-800/50 rounded-lg p-3 border border-zinc-700">
                  <p className="font-medium text-white">{completingTask?.title}</p>
                  {completingTask?.description && (
                    <p className="text-sm text-zinc-400 mt-1">{completingTask.description}</p>
                  )}
                </div>

                {/* Hours */}
                <div>
                  <label className="text-sm text-zinc-400 mb-2 block flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Quante ore hai lavorato su questo task?
                  </label>
                  <div className="flex gap-2 mb-2">
                    {[1, 2, 3, 4, 5, 6, 8].map((h) => (
                      <Button
                        key={h}
                        variant={taskHours === h.toString() ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setTaskHours(h.toString())}
                        className={taskHours === h.toString() ? 'bg-primary text-black' : 'border-zinc-700 text-zinc-400'}
                      >
                        {h}h
                      </Button>
                    ))}
                  </div>
                  <Input
                    type="number"
                    step="0.5"
                    min="0.5"
                    value={taskHours}
                    onChange={(e) => setTaskHours(e.target.value)}
                    placeholder="Ore lavorate"
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="task-hours-input"
                  />
                </div>

                {/* Date */}
                <div>
                  <label className="text-sm text-zinc-400 mb-2 block flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Quando hai lavorato?
                  </label>
                  <Input
                    type="date"
                    value={taskDate}
                    onChange={(e) => setTaskDate(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="task-date-input"
                  />
                </div>

                {/* Commessa */}
                <div>
                  <label className="text-sm text-zinc-400 mb-2 block flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    Per quale commessa?
                  </label>
                  <Select onValueChange={setTaskCommessa} value={taskCommessa}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="task-commessa-select">
                      <SelectValue placeholder="Seleziona commessa..." />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      {(Array.isArray(commesse) ? commesse : []).map((c) => (
                        <SelectItem key={c.code} value={c.code} className="text-white">
                          <span className="font-mono text-xs text-zinc-500 mr-2">{c.code}</span>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Description */}
                <div>
                  <label className="text-sm text-zinc-400 mb-2 block">Descrizione attività</label>
                  <Textarea
                    value={taskDescription}
                    onChange={(e) => setTaskDescription(e.target.value)}
                    placeholder="Cosa hai fatto..."
                    className="bg-zinc-800 border-zinc-700 text-white min-h-[80px]"
                    data-testid="task-description-input"
                  />
                </div>

                {/* Submit */}
                <Button
                  className="w-full bg-green-600 hover:bg-green-500 text-white font-bold py-6 text-lg"
                  onClick={handleCompleteTask}
                  disabled={taskLoading || !taskHours || !taskCommessa}
                  data-testid="submit-task-completion"
                >
                  {taskLoading ? (
                    'Registrando...'
                  ) : (
                    <>
                      <CheckCircle2 className="w-5 h-5 mr-2" />
                      COMPLETA E REGISTRA ORE
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Task Detail Modal */}
      <Dialog open={!!selectedTaskDetail} onOpenChange={() => setSelectedTaskDetail(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <Badge className={cn(
                "text-[10px] w-fit font-black tracking-tighter",
                selectedTaskDetail?.status === 'pending' ? 'bg-red-500/20 text-red-400' :
                  selectedTaskDetail?.status === 'in_progress' ? 'bg-amber-500/20 text-amber-400' :
                  selectedTaskDetail?.status === 'waiting' ? 'bg-blue-500/20 text-blue-400' :
                  'bg-green-500/20 text-green-400'
              )}>
                {selectedTaskDetail?.status === 'pending' ? 'IN ATTESA' :
                  selectedTaskDetail?.status === 'in_progress' ? 'IN CORSO' :
                  selectedTaskDetail?.status === 'waiting' ? 'IN PAUSA' : 'COMPLETATO'}
              </Badge>
              {selectedTaskDetail?.due_date && selectedTaskDetail?.status !== 'waiting' && (
                <span className="text-xs font-bold text-zinc-500 flex items-center gap-1">
                  <Timer className="w-3 h-3" />
                  Scadenza: {selectedTaskDetail.due_date.split('-').reverse().join('/')}
                </span>
              )}
            </div>
            {selectedTaskDetail?.status === 'waiting' && selectedTaskDetail?.pending_reason && (
              <div className="flex items-center gap-1.5 mt-1">
                <PauseCircle className="w-3 h-3 text-blue-400/70" />
                <span className="text-xs text-blue-300/70 italic">{selectedTaskDetail.pending_reason}</span>
              </div>
            )}
            <DialogTitle className="font-display text-2xl text-white">
              {selectedTaskDetail?.title}
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              Assegnato da {selectedTaskDetail?.assigned_by_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 pt-4">
            {/* Description */}
            <div>
              <h4 className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">Descrizione</h4>
              <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700 text-zinc-300 whitespace-pre-wrap leading-relaxed">
                {selectedTaskDetail?.description || 'Nessuna descrizione fornita.'}
              </div>
            </div>

            {/* Don Threat */}
            {selectedTaskDetail?.don_threat && (
              <div className="bg-red-950/20 border border-red-500/20 rounded-xl p-4">
                <div className="flex items-center gap-2 text-red-400 mb-2">
                  <Skull className="w-4 h-4" />
                  <span className="text-xs font-bold uppercase tracking-widest">Messaggio del Don</span>
                </div>
                <p className="text-sm text-red-300 italic">
                  "{selectedTaskDetail.don_threat}"
                </p>
              </div>
            )}

            {/* Links Section */}
            {selectedTaskDetail?.links && selectedTaskDetail.links.length > 0 && (
              <div>
                <h4 className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                  <LinkIcon className="w-3 h-3" />
                  Link e Riferimenti
                </h4>
                <div className="grid gap-2">
                  {selectedTaskDetail.links.map((linkStr, i) => {
                    const parts = linkStr.split('|');
                    let label = 'Link';
                    let url = '#';
                    if (parts.length === 2) {
                      label = parts[0];
                      url = parts[1];
                    } else {
                      url = linkStr;
                    }

                    return (
                      <a
                        key={i}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between p-3 bg-zinc-800/30 hover:bg-zinc-800 border border-zinc-700 hover:border-blue-500/50 rounded-xl transition-all group"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-400">
                            <LinkIcon className="w-4 h-4" />
                          </div>
                          <span className="font-bold text-white">{label}</span>
                        </div>
                        <ExternalLink className="w-4 h-4 text-zinc-600 group-hover:text-blue-400 transition-colors" />
                      </a>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Emails/References if any */}
            {selectedTaskDetail?.emails && selectedTaskDetail.emails.length > 0 && (
              <div>
                <h4 className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                  <MessageSquare className="w-3 h-3" />
                  Email Riferimento
                </h4>
                <div className="space-y-1">
                  {selectedTaskDetail.emails.map((email, i) => (
                    <div key={i} className="text-sm text-zinc-400 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-zinc-700" />
                      {email}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions in Modal */}
            <div className="flex gap-3 pt-4 border-t border-zinc-800">
              {(selectedTaskDetail?.status === 'pending' || selectedTaskDetail?.status === 'waiting') && (
                <Button
                  className="flex-1 bg-amber-600 hover:bg-amber-500 text-white font-bold"
                  onClick={async () => {
                    const res = await updateTaskStatus(selectedTaskDetail.id, 'in_progress');
                    if (res.success) {
                      toast.success("🔥 ORDINE INIZIATO");
                      setSelectedTaskDetail(prev => ({ ...prev, status: 'in_progress' }));
                    }
                  }}
                >
                  <Timer className="w-4 h-4 mr-2" />
                  {selectedTaskDetail?.status === 'waiting' ? 'RIPRENDI' : 'INIZIA'}
                </Button>
              )}
              <Button
                className="flex-1 bg-green-600 hover:bg-green-500 text-white font-bold"
                onClick={() => {
                  const task = selectedTaskDetail;
                  setSelectedTaskDetail(null);
                  openTaskCompletion(task);
                }}
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                CONSEGNA 🤌
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Negotiation Modal */}
      <NegotiationModal
        task={negotiatingTask}
        user={user}
        isOpen={!!negotiatingTask}
        onClose={() => setNegotiatingTask(null)}
        onSuccess={handleNegotiationSuccess}
      />

      {/* Pending Reason Modal */}
      <PendingReasonModal
        task={pausingTask}
        isOpen={!!pausingTask}
        onClose={() => setPausingTask(null)}
        onConfirm={handlePauseConfirm}
      />

      {/* Achievement Celebration Popup */}
      <AchievementPopup
        achievement={newAchievement}
        onDismiss={dismissNewAchievement}
      />

      {/* Footer */}
      <footer className="border-t border-zinc-800 p-6 text-center mt-8">
        <p className="text-2xl mb-2">🤌🇫🇷</p>
        <p className="text-zinc-500 text-sm font-display">Don Doit © 2025 - La Famiglia QuiteBold</p>
        <p className="text-xs text-zinc-700 mt-1 italic">"Don't do it? Mais non, DO IT, SUBITO!"</p>
      </footer>

      {/* Active Time Tracker bar - rendered outside header to avoid backdrop-blur breaking fixed positioning */}
      {activeTask && activeTask.status === 'active' && (
        <TimeTracker
          activeTask={activeTask}
          startTracking={startTracking}
          finishTracking={finishTracking}
          commesse={commesse}
          user={user}
        />
      )}
    </div>
  );
};

export default MemberDashboard;
